import React, { useState } from 'react';
import { WhichWeightAPI } from 'WhichWeightAPI'

import ScreenFrame from 'components/Frame/ScreenFrame';
import LoadingButton from 'components/LoadingButton';

export default function CreateCustomExercise() {

    const [formData, setFormData] = useState({
        exerciseName: '',
        exerciseVideo: null,
        exerciseThumbnail: null,
        exerciseInstructions: '',
        exerciseLateral: 'bilateral',
        exerciseType: 'bodyweight'
    });

    const [validation, setValidation] = useState({
        exerciseName: true,
        exerciseVideo: true,
        exerciseThumbnail: true,
        exerciseInstructions: true,
        exerciseLateral: true,
        exerciseType: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setValidation(prev => ({
            ...prev,
            [field]: true
        }));
    };

    const validateForm = () => {
        const newValidation = {
            exerciseName: formData.exerciseName.length > 0 && formData.exerciseName.length <= 100,
            exerciseVideo: formData.exerciseVideo === null || (formData.exerciseVideo.size <= 5 * 1024 * 1024),
            exerciseThumbnail: formData.exerciseThumbnail === null || (formData.exerciseThumbnail.size <= 1 * 1024 * 1024),
            exerciseInstructions: formData.exerciseInstructions.length <= 500,
            exerciseLateral: true,
            exerciseType: true
        };

        setValidation(newValidation);
        return Object.values(newValidation).every(v => v);
    };

    const submitExercise = async (formData) => {
        try {
            const videoBase64 = formData.exerciseVideo ? await convertFileToBase64(formData.exerciseVideo) : '';
            const imageBase64 = formData.exerciseThumbnail ? await convertFileToBase64(formData.exerciseThumbnail) : '';
            
            const requestBody = {
                exerciseName: formData.exerciseName,
                exerciseType: formData.exerciseType,
                exerciseLateral: formData.exerciseLateral,
                exerciseVideo: videoBase64,
                exerciseThumbnail: imageBase64,
                exerciseInstructions: formData.exerciseInstructions || ''
            };
    
            const response = await WhichWeightAPI(
                'CORE', 
                'POST', 
                '/exercise/custom', 
                requestBody
            );
    
            if (response === "failure" || !response.data) {
                throw new Error('Failed to create exercise');
            }
    
            return response.data;
            
        } catch (error) {
            if (error.response) {
                // Check for 413 Payload Too Large error
                if (error.response.status === 413) {
                    throw new Error('File size too large. Please use smaller files (max 5MB for video, 1MB for thumbnail).');
                }
                const errorMessage = error.response.data?.error || 'Server error occurred';
                throw new Error(`Server error: ${errorMessage}`);
            } else if (error.request) {
                throw new Error('No response from server. Please check your connection.');
            } else {
                throw error;
            }
        }
    };
    
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                resolve(null);
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data:image/png;base64, prefix
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
    
        setIsSubmitting(true);
        try {
            const result = await submitExercise(formData);
            if (result) {
                alert('Exercise created successfully!');
                setFormData({
                    exerciseName: '',
                    exerciseVideo: null,
                    exerciseThumbnail: null,
                    exerciseInstructions: '',
                    exerciseLateral: 'bilateral',
                    exerciseType: 'bodyweight'
                });
            }
        } catch (error) {
            console.error('Error submitting exercise:', error);
            alert(error.message || 'Failed to create exercise. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ScreenFrame>
            <div className="border-b border-gray-900/10 pb-12">
                <h1 className="text-xl font-bold leading-7 text-gray-900">Create Custom Exercise</h1>
                <p className="mt-1 text-md leading-6 text-gray-600">
                    Create a new custom exercise with video, thumbnail, instructions & more.
                </p>
                <p className="mt-1 text-sm text-orange-600">
                    Note: For best results, use videos under 5MB and thumbnails under 1MB.
                </p>

                <form onSubmit={handleSubmit} className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                    {/* Exercise Name */}
                    <div className="sm:col-span-full">
                        <label htmlFor="exerciseName" className="block text-sm font-medium leading-6 text-gray-900">
                            Exercise Name*
                        </label>
                        <div className="mt-2">
                            <input
                                type="text"
                                id="exerciseName"
                                name="exerciseName"
                                className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                value={formData.exerciseName}
                                onChange={(e) => handleInputChange('exerciseName', e.target.value)}
                                maxLength={100}
                            />
                        </div>
                        {!validation.exerciseName && (
                            <div className="mt-1 text-xs text-red-400">
                                Exercise name is required and must be 100 characters or less
                            </div>
                        )}
                    </div>

                    {/* Exercise Video */}
                    <div className="sm:col-span-full">
                        <label htmlFor="exerciseVideo" className="block text-sm font-medium leading-6 text-gray-900">
                            Exercise Video (.mp4)
                        </label>
                        <div className="mt-2">
                            <input
                                type="file"
                                id="exerciseVideo"
                                name="exerciseVideo"
                                accept=".mp4"
                                className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                onChange={(e) => handleInputChange('exerciseVideo', e.target.files[0])}
                            />
                        </div>
                        {!validation.exerciseVideo && (
                            <div className="mt-1 text-xs text-red-400">
                                Video is required and must be 5MB or less
                            </div>
                        )}
                    </div>

                    {/* Exercise Thumbnail */}
                    <div className="sm:col-span-full">
                        <label htmlFor="exerciseThumbnail" className="block text-sm font-medium leading-6 text-gray-900">
                            Exercise Thumbnail (.png)
                        </label>
                        <div className="mt-2">
                            <input
                                type="file"
                                id="exerciseThumbnail"
                                name="exerciseThumbnail"
                                accept=".png"
                                className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                onChange={(e) => handleInputChange('exerciseThumbnail', e.target.files[0])}
                            />
                        </div>
                        {!validation.exerciseThumbnail && (
                            <div className="mt-1 text-xs text-red-400">
                                Thumbnail is required and must be 1MB or less
                            </div>
                        )}
                    </div>

                    {/* Exercise Instructions */}
                    <div className="sm:col-span-full">
                        <label htmlFor="exerciseInstructions" className="block text-sm font-medium leading-6 text-gray-900">
                            Exercise Instructions
                        </label>
                        <div className="mt-2">
                            <textarea
                                id="exerciseInstructions"
                                name="exerciseInstructions"
                                rows={4}
                                className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                value={formData.exerciseInstructions}
                                onChange={(e) => handleInputChange('exerciseInstructions', e.target.value)}
                                maxLength={500}
                            />
                        </div>
                        {!validation.exerciseInstructions && (
                            <div className="mt-1 text-xs text-red-400">
                                Instructions are required and must be 500 characters or less
                            </div>
                        )}
                    </div>

                    {/* Exercise Lateral */}
                    <div className="sm:col-span-full">
                        <label htmlFor="exerciseLateral" className="block text-sm font-medium leading-6 text-gray-900">
                            Exercise Lateral*
                        </label>
                        <div className="mt-2">
                            <select
                                id="exerciseLateral"
                                name="exerciseLateral"
                                className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                value={formData.exerciseLateral}
                                onChange={(e) => handleInputChange('exerciseLateral', e.target.value)}
                            >
                                <option value="bilateral">Bilateral</option>
                                <option value="unilateral">Unilateral</option>
                            </select>
                        </div>
                    </div>

                    {/* Exercise Type */}
                    <div className="sm:col-span-full">
                        <label htmlFor="exerciseType" className="block text-sm font-medium leading-6 text-gray-900">
                            Exercise Type*
                        </label>
                        <div className="mt-2">
                            <select
                                id="exerciseType"
                                name="exerciseType"
                                className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                value={formData.exerciseType}
                                onChange={(e) => handleInputChange('exerciseType', e.target.value)}
                            >
                                <option value="bodyweight">Bodyweight</option>
                                <option value="recommendation">Recommendation</option>
                            </select>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="sm:col-span-full">
                        <LoadingButton
                            buttonText="Create Exercise"
                            isLoading={isSubmitting}
                            action={handleSubmit}
                        />
                    </div>
                </form>
            </div>
        </ScreenFrame>
    );
} 